import React, { useState } from 'react';
import { upperFirst } from 'lodash';
import { Button, ColorPicker, Popover, Select, Tooltip } from 'antd';
import { UndoOutlined, WarningOutlined } from '@ant-design/icons';
import DesignTokenPicker, { TokenPreview } from 'admin/components/common/DesignTokenPicker';
import FillPreview from 'admin/components/pages/Story/CardEditor/Inspector/PropField/FillPreview';
import { useTheme } from 'admin/components/common/Theme';
import { Icon, type IconType } from 'admin/components/common/Icon';
import TokenPill, { TokenTooltip } from 'admin/components/pages/Story/CardEditor/Inspector/PropField/TokenPill';
import { formatTokenDisplayValue } from 'admin/components/pages/Story/CardEditor/Inspector/PropField/token-display-value';
import {
	DESIGN_PRESET_ENUM_VALUES,
	getCompatibleDesignTokens,
	resolveDesignModeValue,
	getDesignPresetTokenId,
	getDesignPresetInheritedAssignment,
	normalizeDesignPresetValue,
	resolveDesignPresetTokenParts,
	DESIGN_TOKEN_PROPERTY_CONFIG,
	type DesignDefinition,
	type DesignPreset,
	type DesignPresetProperty,
	type DesignPresetValue,
} from 'common/design-tokens';
import { parseDesignPresetShadows } from 'common/design-tokens/shadows';
import t from 'utils/translate';
import type { StoryDefaultPlatform, StoryMediaQuery } from 'types/story';
import {
	buildFontFamilySelectOptions,
	isFontFamilyAvailable,
	type FontFamilySelectOption,
	type FontFamilySelectOptionGroup,
} from 'admin/components/common/Form/font-family-options';
import FontFamilyUnavailableOption from 'admin/components/common/Form/FontFamilyUnavailableOption';
import { getDesignPresetDisplayValue, getDesignPresetPlatformName } from './design-preset-display';
import {
	formatPresetNumber,
	getPresetCellSource,
	getPresetLiteralKind,
	getPresetNumberStep,
	getPresetNumberUnit,
	parsePresetNumberInput,
	type PresetNumberUnit,
} from './design-preset-cell';
import { ShadowEditor } from './ShadowControl';
import DesignPresetEditorHeader from './DesignPresetEditorHeader';
import css from './DesignPresetsEditor.scss';

export const defaultLiteral = (property: DesignPresetProperty, design: DesignDefinition): DesignPresetValue => {
	if (property === 'fontFamily') return design.defaults.root.fontFamily.base;
	if (property === 'fontWeight') return 400;
	if (property === 'lineHeight') return 1.5;
	if (property === 'color' || property === 'backgroundColor' || property === 'borderColor') return 'rgba(0, 0, 0, 1)';
	if (property === 'opacity') return 1;
	if (property === 'boxShadow' || property === 'textShadow') return 'none';
	if (property in DESIGN_PRESET_ENUM_VALUES) {
		const values: readonly string[] = DESIGN_PRESET_ENUM_VALUES[property as keyof typeof DESIGN_PRESET_ENUM_VALUES];
		return values.includes('none') ? 'none' : values[0];
	}
	return property === 'fontSize' ? '18px' : '0px';
};

const errorMessage = (issue: unknown) => (issue instanceof Error ? issue.message : String(issue));

/**
 * A number typed in place, as in the Tokens tab: Enter or leaving the field saves, Esc restores, arrows step.
 * Nothing is written unless the text changed, so opening an inherited value never turns it into an override.
 */
function NumberLiteral({
	property,
	value,
	placeholder,
	label,
	hint,
	disabled,
	onCommit,
}: {
	property: DesignPresetProperty;
	value: DesignPresetValue | undefined;
	placeholder: string;
	label: string;
	/** The value's trail, shown on the resting value only so it never covers the field being typed in */
	hint: React.ReactNode;
	disabled?: boolean;
	onCommit: (value: DesignPresetValue) => void;
}) {
	const [draft, setDraft] = useState<{ text: string; unit: PresetNumberUnit; dirty: boolean; error?: string }>();
	const text = formatPresetNumber(value);
	if (!draft)
		return (
			<Tooltip title={hint} mouseEnterDelay={0.5}>
				<button
					type="button"
					className={css.literal}
					aria-label={label}
					disabled={disabled}
					onClick={() => setDraft({ text, unit: getPresetNumberUnit(property, value), dirty: false })}
				>
					{text || placeholder}
				</button>
			</Tooltip>
		);
	const save = (closeOnError: boolean) => {
		const next = draft.dirty ? parsePresetNumberInput(draft.text, draft.unit) : undefined;
		if (next === undefined) return setDraft(undefined);
		try {
			onCommit(normalizeDesignPresetValue(property, next));
			return setDraft(undefined);
		} catch (issue) {
			return setDraft(closeOnError ? undefined : { ...draft, error: errorMessage(issue) });
		}
	};
	return (
		<span className={css.numberEdit} data-error={!!draft.error} title={draft.error}>
			<input
				// The cell was clicked to edit, so the field takes focus as the Tokens tab's inputs do
				// eslint-disable-next-line jsx-a11y/no-autofocus
				autoFocus
				aria-label={label}
				inputMode="decimal"
				value={draft.text}
				onFocus={event => event.target.select()}
				onChange={event => setDraft({ ...draft, text: event.target.value, dirty: true, error: undefined })}
				onBlur={() => save(true)}
				onKeyDown={event => {
					if (event.key === 'Enter') save(false);
					if (event.key === 'Escape') {
						event.stopPropagation();
						setDraft(undefined);
					}
					if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
						event.preventDefault();
						const step = getPresetNumberStep(property, draft.unit) * (event.shiftKey ? 10 : 1);
						const current = Number.parseFloat(draft.text) || 0;
						const next = current + (event.key === 'ArrowUp' ? step : -step);
						setDraft({ ...draft, text: String(Number(next.toFixed(2))), dirty: true, error: undefined });
					}
				}}
			/>
			{property === 'lineHeight' ? (
				<button
					type="button"
					className={css.unitToggle}
					aria-label={draft.unit === 'px' ? 'Line height in px' : 'Line height as a multiple'}
					// Keeps the input focused, so switching the unit does not save first
					onMouseDown={event => event.preventDefault()}
					onClick={() =>
						setDraft({
							...draft,
							unit: draft.unit === 'px' ? 'unitless' : 'px',
							dirty: true,
							error: undefined,
						})
					}
				>
					{draft.unit === 'px' ? 'px' : '×'}
				</button>
			) : null}
		</span>
	);
}

const enumLabel = (property: DesignPresetProperty, value: string) => {
	if (property === 'borderStyle') return t(`presets.borderStyles.${value}`);
	if (property === 'direction') return t(`presets.directions.${value}`);
	return upperFirst(value);
};

export default function AssignmentCell({
	property,
	display,
	assigned,
	inherited,
	design,
	fonts,
	modeSelection,
	disabled,
	isBase,
	assignmentLabel,
	mixed = false,
	enumOptions,
	variant = 'cell',
	icon,
	editorHeader,
	onChange,
}: {
	property: DesignPresetProperty;
	display: ReturnType<typeof getDesignPresetDisplayValue>;
	assigned: DesignPresetValue | undefined;
	inherited?: { value: DesignPresetValue; source: string };
	design: DesignDefinition;
	fonts: { fontFamily: string }[];
	modeSelection: Record<string, string>;
	disabled?: boolean;
	isBase: boolean;
	assignmentLabel?: string;
	mixed?: boolean;
	/** Options in place of the property's enum values, e.g. the Border group's three line styles */
	enumOptions?: { value: string; label: React.ReactNode }[];
	/**
	 * `field` is an Inspector field in a group editor: the token button over the icon, a color as its swatch only, and
	 * no revert, since a group resets as a whole from its table cell
	 */
	variant?: 'cell' | 'field';
	/** The field's leading icon, such as a side or a corner */
	icon?: IconType;
	/** Heads a popover editor, such as the shadow layers, with the property and its platform */
	editorHeader?: React.ReactNode;
	onChange: (value: DesignPresetValue | undefined) => void;
}) {
	const [panel, setPanel] = useState<'tokens' | 'shadow'>();
	const { theme } = useTheme();
	const [search, setSearch] = useState('');
	const compatible = getCompatibleDesignTokens(design.tokens, property);
	const effective = mixed ? undefined : assigned ?? inherited?.value;
	const tokenId = getDesignPresetTokenId(effective);
	const token = tokenId ? compatible.find(item => item.id === tokenId) : undefined;
	const tokenValue = token ? resolveDesignModeValue<string | number>(token.value, modeSelection) : undefined;
	const exactValue = display?.value ?? tokenValue ?? effective;
	const source = getPresetCellSource({ assigned, inherited: mixed ? undefined : inherited, isBase });
	const kind = getPresetLiteralKind(property);
	const supportsTokens = property in DESIGN_TOKEN_PROPERTY_CONFIG;
	const fontOptions = fonts.map(font => ({ value: font.fontFamily, label: font.fontFamily }));
	const fontFamily = token ? tokenValue : tokenId ? undefined : exactValue;
	const unavailableFont =
		property === 'fontFamily' && typeof fontFamily === 'string' && !isFontFamilyAvailable(fontFamily, fontOptions);
	const unresolved = (!!tokenId && !token) || unavailableFont;
	const name = assignmentLabel ?? t(`presets.properties.${property}`);
	const inheritedHint =
		source === 'inherited' && inherited ? `${t('presets.inherited')} ${inherited.source}` : undefined;
	const label = `${name}: ${mixed ? 'Mixed' : String(exactValue ?? '—')}${inheritedHint ? ` (${inheritedHint})` : ''}`;
	const empty = mixed ? 'Mixed' : '—';
	const closePanel = () => setPanel(undefined);
	const openTokens = () => {
		setSearch('');
		setPanel('tokens');
	};
	const converted = !!display && display.cssValue !== display.authoredValue;
	// A tooltip only when it says more than the cell: where the value comes from, how it converts, or what is wrong
	const hint = mixed ? (
		'Different values. Type one to apply it to all sides.'
	) : inheritedHint || converted || unresolved ? (
		<>
			{token ? <TokenPreview token={token} value={exactValue} /> : tokenId ?? String(exactValue ?? '—')}
			{converted ? <div>{`${display.authoredValue} → ${display.cssValue} → ${display.value}`}</div> : null}
			{inheritedHint ? <div>{inheritedHint}</div> : null}
			{tokenId && !token ? <div>{t('presets.unresolved')}</div> : null}
			{unavailableFont ? <div>{t('presets.unavailable')}</div> : null}
		</>
	) : undefined;

	let value: React.ReactNode;
	if (tokenId) {
		const tokenText = token
			? formatTokenDisplayValue(token.type, String(exactValue))
			: t('designTokens.unresolved');
		value = (
			<TokenPill
				label={`${t('designTokens.choose')}: ${label}`}
				swatch={token?.type === 'color' ? String(exactValue) : undefined}
				value={tokenText}
				tooltip={
					<>
						<TokenTooltip
							name={token?.name ?? tokenId}
							value={token ? String(tokenValue) : t('designTokens.unresolved')}
						/>
						{inheritedHint ? <div>{inheritedHint}</div> : null}
					</>
				}
				unresolved={!token}
				open={panel === 'tokens'}
				disabled={disabled}
				onClick={openTokens}
				onUnlink={
					assigned !== undefined ? () => onChange(tokenValue ?? defaultLiteral(property, design)) : undefined
				}
			/>
		);
	} else if (kind === 'number') {
		value = (
			<NumberLiteral
				property={property}
				value={exactValue}
				placeholder={empty}
				label={label}
				hint={hint}
				disabled={disabled}
				onCommit={onChange}
			/>
		);
	} else if (kind === 'enum') {
		const values: readonly string[] = DESIGN_PRESET_ENUM_VALUES[property as keyof typeof DESIGN_PRESET_ENUM_VALUES];
		value = (
			<Select
				variant="borderless"
				size="small"
				className={css.cellSelect}
				popupMatchSelectWidth={false}
				aria-label={label}
				disabled={disabled}
				placeholder={empty}
				value={exactValue === undefined ? undefined : String(exactValue)}
				options={enumOptions ?? values.map(item => ({ value: item, label: enumLabel(property, item) }))}
				onChange={onChange}
			/>
		);
	} else if (kind === 'fontFamily') {
		const family = exactValue === undefined ? '' : String(exactValue);
		const select = buildFontFamilySelectOptions(family, fontOptions);
		value = (
			<Select<string, FontFamilySelectOption | FontFamilySelectOptionGroup>
				variant="borderless"
				size="small"
				className={css.cellSelect}
				popupMatchSelectWidth={false}
				aria-label={label}
				disabled={disabled}
				showSearch
				optionFilterProp="label"
				placeholder={empty}
				value={select.value || undefined}
				options={select.options}
				labelRender={({ label: optionLabel }) =>
					select.unavailable ? <FontFamilyUnavailableOption family={family} compact /> : optionLabel
				}
				optionRender={option =>
					'unavailable' in option.data && option.data.unavailable ? (
						<FontFamilyUnavailableOption family={String(option.data.value)} />
					) : (
						option.label
					)
				}
				onChange={onChange}
			/>
		);
	} else if (kind === 'color') {
		const color = exactValue === undefined ? undefined : String(exactValue);
		value = (
			<ColorPicker disabled={disabled} value={color} onChangeComplete={next => onChange(next.toRgbString())}>
				<button type="button" className={css.literal} aria-label={label} disabled={disabled}>
					{variant === 'field' ? (
						// The Inspector's color field: the swatch alone, its empty state when unset
						<FillPreview value={color ?? ''} />
					) : (
						<>
							{color ? <FillPreview value={color} className={css.swatch} /> : null}
							{color ? formatTokenDisplayValue('color', color) : empty}
						</>
					)}
				</button>
			</ColorPicker>
		);
	} else {
		let layers = 0;
		try {
			layers = parseDesignPresetShadows(
				property as 'boxShadow' | 'textShadow',
				String(exactValue ?? 'none')
			).length;
		} catch {
			/* Unparsed CSS still opens the editor, where it can be removed or replaced */
		}
		value = (
			<button
				type="button"
				className={css.literal}
				aria-label={label}
				disabled={disabled}
				onClick={() => setPanel('shadow')}
			>
				{layers ? (
					<>
						<span
							className={css.shadowSample}
							data-property={property}
							style={property === 'boxShadow' ? { boxShadow: String(exactValue) } : undefined}
						>
							{property === 'textShadow' ? (
								<span style={{ textShadow: String(exactValue) }}>A</span>
							) : null}
						</span>
						{layers === 1 ? '1 shadow' : `${layers} shadows`}
					</>
				) : exactValue === undefined ? (
					empty
				) : (
					'None'
				)}
			</button>
		);
	}

	const panelContent =
		panel === 'tokens' ? (
			<DesignTokenPicker
				dark={theme === 'dark'}
				search={search}
				onSearchChange={setSearch}
				onClose={closePanel}
				modeSelection={modeSelection}
				sources={[
					{
						id: 'design',
						tokens: compatible,
						selectedTokenId: tokenId,
						onSelect: reference => {
							onChange(reference);
							closePanel();
						},
					},
				]}
				footer={
					tokenId ? (
						<Button
							type="text"
							onClick={() => {
								onChange(tokenValue ?? defaultLiteral(property, design));
								closePanel();
							}}
						>
							{t('designTokens.unlink')}
						</Button>
					) : undefined
				}
			/>
		) : panel === 'shadow' ? (
			<ShadowEditor
				property={property as 'boxShadow' | 'textShadow'}
				// As stored, so a layer's linked color stays a reference
				value={effective ?? defaultLiteral(property, design)}
				header={editorHeader ?? <header className={css.groupEditorHeader}>{name}</header>}
				source={source}
				paint={color => resolveDesignPresetTokenParts(color, design, modeSelection)}
				renderColor={(color, onColorChange) => (
					<AssignmentCell
						property="color"
						display={undefined}
						assigned={source === 'inherited' ? undefined : color || undefined}
						inherited={
							source === 'inherited' && inherited && color ? { ...inherited, value: color } : undefined
						}
						design={design}
						fonts={fonts}
						modeSelection={modeSelection}
						disabled={disabled}
						isBase={isBase}
						assignmentLabel="Shadow color"
						variant="field"
						onChange={next => {
							if (next !== undefined) onColorChange(String(next));
						}}
					/>
				)}
				onChange={onChange}
				onClose={closePanel}
			/>
		) : null;

	return (
		<Popover
			trigger="click"
			placement="bottomLeft"
			open={!!panel && !disabled}
			onOpenChange={next => {
				if (!next) closePanel();
			}}
			styles={{ container: { padding: 0 } }}
			content={panelContent}
		>
			<div
				className={css.assignment}
				data-source={source}
				data-open={!!panel}
				data-unresolved={unresolved}
				data-variant={variant}
				data-kind={kind}
				data-icon={!!icon}
			>
				{/* The value's trail sits on a delay, so the field stays quiet to click */}
				{/* Tokens show their own tooltip, and a number cell shows the hint on its resting value */}
				<Tooltip
					title={tokenId || kind === 'number' ? undefined : hint}
					mouseEnterDelay={0.5}
					open={panel ? false : undefined}
				>
					<span className={css.cellValue}>
						{icon ? (
							<Icon type={icon} width={16} height={16} color="currentColor" className={css.fieldIcon} />
						) : null}
						{value}
						{unavailableFont ? <WarningOutlined className={css.cellWarning} /> : null}
					</span>
				</Tooltip>
				<span className={css.cellActions}>
					{supportsTokens && !tokenId ? (
						<Tooltip title={t('designTokens.connect')}>
							<button
								type="button"
								className={css.connectToken}
								disabled={disabled}
								aria-label={t('designTokens.connect')}
								onClick={openTokens}
							/>
						</Tooltip>
					) : null}
					{variant === 'cell' && assigned !== undefined ? (
						<Tooltip title={t(isBase ? 'presets.clearAssignment' : 'presets.revert')}>
							<button
								type="button"
								className={css.revert}
								disabled={disabled}
								aria-label={t(isBase ? 'presets.clearAssignment' : 'presets.revert')}
								onClick={() => {
									closePanel();
									onChange(undefined);
								}}
							>
								<UndoOutlined />
							</button>
						</Tooltip>
					) : null}
				</span>
			</div>
		</Popover>
	);
}

export function PlatformAssignment({
	property,
	preset,
	platform,
	design,
	mediaQuery,
	modeSelection,
	fonts,
	disabled,
	assignmentLabel,
	variant,
	icon,
	onChange,
}: {
	property: DesignPresetProperty;
	preset: DesignPreset;
	platform: 'base' | StoryDefaultPlatform;
	design: DesignDefinition;
	mediaQuery: StoryMediaQuery;
	modeSelection: Record<string, string>;
	fonts: { fontFamily: string }[];
	disabled?: boolean;
	assignmentLabel?: string;
	variant?: 'cell' | 'field';
	icon?: IconType;
	onChange: (next: DesignPresetValue | undefined) => void;
}) {
	const assignment = preset.properties[property] ?? {};
	const inherited =
		platform === 'base' ? undefined : getDesignPresetInheritedAssignment(assignment, platform, mediaQuery);
	return (
		<AssignmentCell
			property={property}
			design={design}
			fonts={fonts}
			modeSelection={modeSelection}
			disabled={disabled}
			isBase={platform === 'base'}
			assignmentLabel={assignmentLabel}
			variant={variant}
			icon={icon}
			editorHeader={
				<DesignPresetEditorHeader
					label={assignmentLabel ?? t(`presets.properties.${property}`)}
					platform={platform}
					mediaQuery={mediaQuery}
				/>
			}
			assigned={platform === 'base' ? assignment.base : assignment.platforms?.[platform]}
			inherited={
				inherited
					? {
							...inherited,
							source:
								inherited.source === 'Base'
									? 'Base'
									: getDesignPresetPlatformName(
											inherited.source,
											mediaQuery.config[inherited.source]?.uiName
										),
						}
					: undefined
			}
			display={getDesignPresetDisplayValue({ property, assignment, platform, mediaQuery, design, modeSelection })}
			onChange={onChange}
		/>
	);
}
